import { Card } from './common'
import type { Column } from './DataTable'
import { DataTable } from './DataTable'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * The certificates the machine's services present, read by the CENSO agent
 * with a local TLS handshake to every listening port (attribute
 * `tls_certificates`): expiry first, then what each one says.
 */
export interface CertificatoTLS {
  port: number
  address?: string
  process?: string
  tls_version?: string
  cipher?: string
  subject: string
  sans?: string[]
  issuer: string
  serial?: string
  not_before: string
  not_after: string
  self_signed: boolean
  key_type?: string
  key_bits?: number
  signature?: string
  sha256: string
  chain_length: number
  chain?: string[]
}

const GIORNO = 24 * 3600 * 1000

function giorni(c: CertificatoTLS): number {
  return Math.floor((new Date(c.not_after).getTime() - Date.now()) / GIORNO)
}

function stato(c: CertificatoTLS) {
  const g = giorni(c)
  if (g < 0) return <span className="badge badge--error">{t('scaduto da {n} giorni', { n: -g })}</span>
  if (g < 30) return <span className="badge badge--warn">{t('scade fra {n} giorni', { n: g })}</span>
  return <span className="badge badge--ok">{t('valido ancora {n} giorni', { n: g })}</span>
}

function cn(dn: string): string {
  const m = /CN=([^,]+)/.exec(dn)
  return m ? m[1] : dn
}

export function SezioneCertificati({ certificati }: { certificati: CertificatoTLS[] }) {
  const deboli = (c: CertificatoTLS) =>
    (c.key_type === 'RSA' && (c.key_bits ?? 0) < 2048) || /SHA1|MD5/i.test(c.signature ?? '') ||
    ['TLS 1.0', 'TLS 1.1'].includes(c.tls_version ?? '')
  const colonne: Column<CertificatoTLS>[] = [
    { key: 'not_after', header: t('Scadenza'), value: (c) => c.not_after, render: (c) => (
      <>{stato(c)}<br /><small className="text-muted">{new Date(c.not_after).toLocaleDateString(localeCorrente())}</small></>
    ) },
    { key: 'port', header: t('Porta'), value: (c) => c.port, render: (c) => (
      <><span className="mono">{c.port}</span>{c.process && <small className="text-muted"> · {c.process}</small>}</>
    ) },
    { key: 'subject', header: t('Soggetto'), value: (c) => c.subject, render: (c) => (
      <>{cn(c.subject)}{(c.sans ?? []).length > 0 && <><br /><small className="text-muted">{(c.sans ?? []).slice(0, 6).join(', ')}{(c.sans ?? []).length > 6 ? '…' : ''}</small></>}</>
    ) },
    { key: 'issuer', header: t('Emittente'), value: (c) => c.issuer, render: (c) => (
      <>{c.self_signed ? <span className="badge badge--warn">{t('autofirmato')}</span> : cn(c.issuer)}
        {c.chain_length > 1 && <><br /><small className="text-muted">{t('catena di {n}', { n: c.chain_length })}</small></>}</>
    ) },
    { key: 'key', header: t('Chiave e protocollo'), value: (c) => `${c.key_type ?? ''} ${c.key_bits ?? ''} ${c.tls_version ?? ''}`, render: (c) => (
      <>
        <span className="mono">{c.key_type} {c.key_bits}</span> · <span className="mono">{c.tls_version}</span>
        {deboli(c) && <> <span className="badge badge--warn">{t('debole')}</span></>}
        <br /><small className="text-muted">{c.signature}</small>
      </>
    ) },
    { key: 'sha256', header: t('Impronta SHA-256'), filter: false, render: (c) => <small className="mono" title={c.sha256}>{c.sha256.slice(0, 16)}…</small> },
  ]
  return (
    <Card title={t('Certificati TLS esposti ({n})', { n: certificati.length })}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('l’agente CENSO esegue un handshake TLS verso le porte in ascolto dell’asset per rilevare i certificati esposti dai servizi e verificarne caratteristiche, validità e scadenza.')}
      </p>
      <DataTable rows={certificati} rowKey={(c) => `${c.port}-${c.sha256}`} columns={colonne} initialSort={{ key: 'not_after' }} />
    </Card>
  )
}
